// @ts-check
import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react"
import IndividualReportTable from "./IndividualReportTable.controller"
import { DateTime } from "luxon"
import {
  convertToCSV,
  downloadCSV,
  getExportFilename,
  hasTasksWithSpecialCategories,
} from "../../../../utils/report.utils"
import { GET_INDIVIDUAL_REPORT_CSV_HEADERS } from "../../../../consts/reports.consts"
import useToast from "../../../../hooks/useToast.hook"
import useIndividualReportFiltersContext from "../../../../hooks/contextConsumers/useIndividualReportFiltersContext.hook"
import { getIndividualTasksOfTeamMemberIdLazy } from "../../../../services/report.service"
import { useIndividualReportTableData } from "./useIndividualReportTableData.hook"

/**
 * @typedef {import("./IndividualReportTable.controller.types").IndividualReportDataTableFilterValues} IndividualReportDataTableFilterValues
 */

const IndividualReportTableContainer = React.forwardRef(
  /**
   * @param {import("./IndividualReportTableContainer.controller.types").IndividualReportTableContainerProps} props
   * @param {React.Ref<import("./IndividualReportTableContainer.controller.types").IndividualReportTableContainerRef>} ref
   */
  (props, ref) => {
    const {
      contextMenuRef,
      setContextMenuTarget,
      updatingTask,
      loading,
      setLoading,
      setSelectedTasks,
      inSelectionMode,
      highlightedTask,
    } = props

    const { setErrorMessage } = useToast()
    const { teamMember, dateRange, period } =
      useIndividualReportFiltersContext()

    const teamMemberId = teamMember.value?.id
    const teamMemberName = teamMember.value?.name
    const teamMemberLastName = teamMember.value?.lastName
    const teamMemberWorkAnniversaryDate = teamMember.value?.workAnniversary
    const [startDate, endDate] = dateRange

    const [filterValues, setFilterValues] = useState(
      /** @type {IndividualReportDataTableFilterValues} */ ({})
    )

    /** @type {React.MutableRefObject<import("./IndividualReportTable.controller.types").IndividualReportTableControllerRef | null >} */
    const tableRef = useRef(null)

    const handleFilterValuesChange = useCallback(
      /** @param {IndividualReportDataTableFilterValues} nextFilterValues */
      (nextFilterValues) => {
        setFilterValues((previousFilterValues) => {
          const previousKey = JSON.stringify(previousFilterValues || {})
          const nextKey = JSON.stringify(nextFilterValues || {})

          return previousKey === nextKey ? previousFilterValues : (
              nextFilterValues
            )
        })
      },
      []
    )

    const {
      tasks,
      totals,
      categories,
      modules,
      projects,
      schemaRules,
      isLoading,
      resetData,
      refreshData,
      fetchNextPage,
      hasNextPage,
      isFetchingNextPage,
    } = useIndividualReportTableData({
      teamMember: teamMember.value,
      dateRange,
      filterValues,
    })

    const infiniteScroll = useMemo(
      () => ({
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        fetchThreshold: 10,
      }),
      [fetchNextPage, hasNextPage, isFetchingNextPage]
    )

    useEffect(() => {
      setLoading?.(isLoading)
    }, [isLoading])

    const fetchCSVTasks = useCallback(async () => {
      const response = await getIndividualTasksOfTeamMemberIdLazy(
        teamMemberId,
        tableRef.current?.getFilterValues() || {},
        tableRef.current?.getSortingValue() || {},
        startDate,
        endDate,
        0,
        Number.MAX_SAFE_INTEGER
      )

      if (!response.success) {
        setErrorMessage({ message: response.message })
      }

      return (
        /** @type {{ tasks: any[] }} */ (
          /** @type {unknown} */ (response.result)
        )?.tasks || []
      )
    }, [teamMemberId, startDate, endDate])

    const formatCSVTasks = useCallback(
      /**
       * @param {any[]} tasks
       */
      (tasks) => {
        return tasks
          .map((task) => ({
            ...task,
            date: DateTime.fromISO(task?.date?.split("T")[0])
              .set({ hour: 12 })
              .toISODate(),
          }))
          .filter((task) => !task.isSubheader)
      },
      []
    )

    useImperativeHandle(
      ref,
      () => ({
        reset: () => {
          tableRef.current?.reset()
          resetData()
          setSelectedTasks([])
        },
        exportCSV: async () => {
          const tasks = await fetchCSVTasks()
          const formattedTasks = formatCSVTasks(tasks)
          const tasksCSV = convertToCSV(
            formattedTasks,
            GET_INDIVIDUAL_REPORT_CSV_HEADERS(
              hasTasksWithSpecialCategories(formattedTasks)
            )
          )
          const exportFilename = getExportFilename(
            `REP_IND_Reporte individual_${teamMemberName?.split(" ").join("")}${teamMemberLastName?.split(" ").join("")}`,
            dateRange,
            period
          )
          downloadCSV(tasksCSV, exportFilename)
        },
        refresh: () => {
          refreshData()
        },
        unselectRow: (taskId) => {
          tableRef.current?.unselectRow(taskId)
        },
      }),
      [
        resetData,
        setSelectedTasks,
        teamMemberId,
        startDate,
        endDate,
        period,
        teamMemberName,
        teamMemberLastName,
        refreshData,
        fetchCSVTasks,
        formatCSVTasks,
      ]
    )

    return (
      <IndividualReportTable
        ref={tableRef}
        value={tasks}
        totals={totals}
        contextMenuRef={contextMenuRef}
        setContextMenuTarget={setContextMenuTarget}
        updatingTask={updatingTask}
        highlightedTask={highlightedTask}
        schemaRules={schemaRules}
        inSelectionMode={inSelectionMode}
        setSelectedTasks={setSelectedTasks}
        infiniteScroll={infiniteScroll}
        teamMemberWorkAnniversaryDate={teamMemberWorkAnniversaryDate}
        categories={categories}
        modules={modules}
        projects={projects}
        loading={loading}
        onFilterValuesChange={handleFilterValuesChange}
      />
    )
  }
)

export default React.memo(IndividualReportTableContainer)
